// The left palette: the widgets available for the active section. Click one to
// add it to the canvas (dropped at the bottom). Collapsible (#7) — collapse it
// to a thin rail to give the canvas more room; the choice is remembered by the
// parent. Drag-to-place is a later polish — click-to-add proves the loop.

import type { LayoutSection } from "@catbento/shared";
import { widgetsForSection } from "../widgets/registry.js";
import { useCanvasStore } from "../store/canvas.js";
import { theme } from "../lib/theme.js";

export default function WidgetPalette({
  section,
  collapsed,
  onToggle,
}: {
  section: LayoutSection;
  collapsed: boolean;
  onToggle: () => void;
}) {
  const addWidget = useCanvasStore((s) => s.addWidget);
  const widgets = widgetsForSection(section);

  // Collapsed: a thin rail with just an expand button.
  if (collapsed) {
    return (
      <aside
        style={{
          width: 40,
          flexShrink: 0,
          background: theme.tile,
          border: `1px solid ${theme.border}`,
          borderRadius: 12,
          padding: "10px 0",
          display: "flex",
          justifyContent: "center",
          alignSelf: "flex-start",
        }}
      >
        <button
          onClick={onToggle}
          aria-label="Expand widgets panel"
          title="Show widgets"
          style={{
            border: "none",
            background: "transparent",
            color: theme.inkMuted,
            fontSize: 16,
            cursor: "pointer",
          }}
        >
          ☰
        </button>
      </aside>
    );
  }

  return (
    <aside
      style={{
        width: 200,
        flexShrink: 0,
        background: theme.tile,
        border: `1px solid ${theme.border}`,
        borderRadius: 12,
        padding: 12,
        alignSelf: "flex-start",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 10,
        }}
      >
        <span style={{ color: theme.inkMuted, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5 }}>
          Widgets
        </span>
        <button
          onClick={onToggle}
          aria-label="Collapse widgets panel"
          title="Hide widgets"
          style={{ border: "none", background: "transparent", color: theme.inkMuted, fontSize: 14, cursor: "pointer" }}
        >
          ◂
        </button>
      </div>
      {widgets.length === 0 ? (
        <div style={{ color: theme.inkFaint, fontSize: 13 }}>None yet for this section.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {widgets.map((w) => (
            <button
              key={w.key}
              onClick={() => addWidget(section, w.key)}
              style={{
                textAlign: "left",
                background: theme.field,
                color: theme.ink,
                border: `1px solid ${theme.border}`,
                borderRadius: 8,
                padding: "9px 10px",
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              + {w.title}
            </button>
          ))}
        </div>
      )}
    </aside>
  );
}
